import { useCallback, useEffect, useState } from 'react'
import { confirmCofa, processCofa } from './api'
import type { LotPlanEntry, ProcessResponse, QueueItem } from './types'

const MAX_CONCURRENT = 3

/**
 * Concurrency-limited upload queue for the CofA batch uploader.
 *
 * Per-file lifecycle:
 *   queued → processing → (needs_vendor | needs_confirm | error)
 *   needs_vendor → user picks vendor → processing (retries with vendor_id)
 *   needs_confirm → user confirms plan → confirming → (applied | error)
 *
 * 2026-07-21 hardening: `/cofa/process` never writes to Odoo, even on a
 * clean match — every processed item lands on `needs_confirm` and only
 * an explicit `confirmPlan()` call (→ `POST /cofa/confirm`) applies it.
 *
 * Concurrency cap matches legacy uploader (3). Processing slots release
 * when items reach a terminal state (applied / error) OR transition to
 * a user-gated state (needs_vendor / needs_confirm).
 *
 * All state reads use `setItems(prev => ...)` updaters so callbacks can
 * be memoized with `useCallback([])` without going stale. The dispatcher
 * runs as an effect on `items` change.
 */
export interface UseCofaUploadQueueResult {
  items: QueueItem[]
  enqueue: (files: File[]) => void
  removeItem: (id: string) => void
  /** Kick off a retry with a user-picked vendor. Caller passes the
   *  File it already has in scope; the hook avoids a stale-state ref read. */
  retryWithVendor: (id: string, vendorId: number, file: File) => void
  /** Apply a plan the user confirmed. Caller passes the plan it already
   *  has in scope. */
  confirmPlan: (id: string, plan: QueueItem['plan']) => void
  /** Patch one lot inside an item's plan — used by the product picker
   *  to apply a pick locally without refetching the whole plan.
   *  Patch-merged into `plan.lots[lotIdx]`. */
  patchLot: (id: string, lotIdx: number, patch: Partial<LotPlanEntry>) => void
  /** Swap the vendor on an item's plan and clear per-lot product
   *  resolution so the confirm view re-prompts for each lot against the
   *  new vendor's catalog. The server re-validates on `/cofa/confirm`. */
  setPlanVendor: (id: string, vendor: { id: number; name: string }) => void
  cancelConfirm: (id: string) => void
  clearTerminal: () => void
  /** True while any item is processing or confirming. */
  busy: boolean
  /** True when at least one item is awaiting user action. */
  needsAttention: boolean
}

function makeId(): string {
  return `cofa-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

function isTerminal(s: QueueItem['status']): boolean {
  return s === 'applied' || s === 'error'
}

function isActive(s: QueueItem['status']): boolean {
  return s === 'processing' || s === 'confirming'
}

function mergeProcessResponse(
  item: QueueItem,
  resp: ProcessResponse,
): QueueItem {
  if (resp.status === 'needs_vendor') {
    return {
      ...item,
      status: 'needs_vendor',
      message: resp.message,
      vendorCandidates: resp.vendor_candidates ?? [],
      extractedVendorName: resp.extracted_vendor_name,
      document: resp.document,
    }
  }
  // `/cofa/process` never applies anymore (2026-07-21 hardening) — every
  // plan, clean or not, stops here for a human confirm via `/cofa/confirm`.
  return {
    ...item,
    status: 'needs_confirm',
    message: resp.message,
    plan: resp.plan,
    vendorLearned: resp.vendor_learned ?? null,
    document: resp.document,
  }
}

export function useCofaUploadQueue(): UseCofaUploadQueueResult {
  const [items, setItems] = useState<QueueItem[]>([])

  // ─── dispatcher effect ────────────────────────────────────────────
  // Runs on every `items` change. Promotes queued items to processing
  // up to MAX_CONCURRENT, then kicks off their `processCofa` calls.
  //
  // The synchronous setItems promotion below is flagged by
  // react-hooks/set-state-in-effect. It's intentional here: the effect's
  // primary job is to TRIGGER external side effects (the processCofa
  // promises) and the state write is the atomic "claim these slots"
  // record that prevents double-dispatch. A useReducer rewrite would
  // satisfy the rule but move the concurrency logic out of render-loop
  // visibility, which is a fair tradeoff we're deferring.
  useEffect(() => {
    const activeCount = items.filter((it) => isActive(it.status)).length
    const slots = Math.max(0, MAX_CONCURRENT - activeCount)
    if (slots === 0) return
    const toStart = items.filter((it) => it.status === 'queued').slice(0, slots)
    if (toStart.length === 0) return

    const idsToStart = new Set(toStart.map((it) => it.id))
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setItems((prev) =>
      prev.map((it) =>
        idsToStart.has(it.id) ? { ...it, status: 'processing' as const } : it,
      ),
    )

    for (const item of toStart) {
      processCofa(item.file)
        .then((resp) => {
          setItems((prev) =>
            prev.map((it) => (it.id === item.id ? mergeProcessResponse(it, resp) : it)),
          )
        })
        .catch((err: unknown) => {
          const msg = err instanceof Error ? err.message : String(err)
          setItems((prev) =>
            prev.map((it) =>
              it.id === item.id ? { ...it, status: 'error', message: msg } : it,
            ),
          )
        })
    }
  }, [items])

  const enqueue = useCallback((files: File[]) => {
    if (files.length === 0) return
    const newItems: QueueItem[] = files
      .filter((f) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name))
      .map((f) => ({ id: makeId(), file: f, status: 'queued' }))
    if (newItems.length === 0) return
    setItems((prev) => [...prev, ...newItems])
  }, [])

  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((it) => it.id !== id))
  }, [])

  const retryWithVendor = useCallback(
    (id: string, vendorId: number, file: File) => {
      setItems((prev) =>
        prev.map((it) =>
          it.id === id
            ? { ...it, status: 'processing' as const, vendorCandidates: undefined }
            : it,
        ),
      )
      processCofa(file, { vendorId })
        .then((resp) => {
          setItems((prev) =>
            prev.map((it) => (it.id === id ? mergeProcessResponse(it, resp) : it)),
          )
        })
        .catch((err: unknown) => {
          const msg = err instanceof Error ? err.message : String(err)
          setItems((prev) =>
            prev.map((it) =>
              it.id === id ? { ...it, status: 'error', message: msg } : it,
            ),
          )
        })
    },
    [],
  )

  const confirmPlan = useCallback((id: string, plan: QueueItem['plan']) => {
    if (!plan) return
    setItems((prev) =>
      prev.map((it) =>
        it.id === id ? { ...it, status: 'confirming' as const } : it,
      ),
    )
    confirmCofa(plan)
      .then((resp) => {
        setItems((prev) =>
          prev.map((it) =>
            it.id === id
              ? {
                  ...it,
                  status: 'applied' as const,
                  appliedLots: resp.lots,
                  plan: undefined,
                }
              : it,
          ),
        )
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err)
        setItems((prev) =>
          prev.map((it) =>
            it.id === id ? { ...it, status: 'error', message: msg } : it,
          ),
        )
      })
  }, [])

  const patchLot = useCallback(
    (id: string, lotIdx: number, patch: Partial<LotPlanEntry>) => {
      setItems((prev) =>
        prev.map((it) => {
          if (it.id !== id || !it.plan) return it
          const lots = it.plan.lots.map((l, i) =>
            i === lotIdx ? { ...l, ...patch } : l,
          )
          return { ...it, plan: { ...it.plan, lots } }
        }),
      )
    },
    [],
  )

  const setPlanVendor = useCallback(
    (id: string, vendor: { id: number; name: string }) => {
      setItems((prev) =>
        prev.map((it) => {
          if (it.id !== id || !it.plan) return it
          // Clear every lot's resolved product state — the old product_ids
          // were resolved against the prior vendor's supplierinfo and are
          // stale once the vendor changes. The per-lot pickers will
          // re-prompt against the new vendor's catalog.
          const lots = it.plan.lots.map((l) => ({
            ...l,
            product_id: null,
            product_name: null,
            issues: [],
          }))
          return {
            ...it,
            vendorLearned: null,   // the person replaced the remembered vendor
            plan: {
              ...it.plan,
              vendor_id: vendor.id,
              vendor_name: vendor.name,
              lots,
            },
          }
        }),
      )
    },
    [],
  )

  const cancelConfirm = useCallback(
    (id: string) => {
      // No server-side rollback — uploaded doc stays, queue item drops.
      setItems((prev) => prev.filter((it) => it.id !== id))
    },
    [],
  )

  const clearTerminal = useCallback(() => {
    setItems((prev) => prev.filter((it) => !isTerminal(it.status)))
  }, [])

  const busy = items.some((it) => isActive(it.status))
  const needsAttention = items.some(
    (it) => it.status === 'needs_vendor' || it.status === 'needs_confirm',
  )

  return {
    items,
    enqueue,
    removeItem,
    retryWithVendor,
    confirmPlan,
    patchLot,
    setPlanVendor,
    cancelConfirm,
    clearTerminal,
    busy,
    needsAttention,
  }
}
